import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { message } from 'antd'
import history from 'routerHistory'
import type { UserManagementState } from '../types'
import {
  userAddProfile,
  userUpdateProfile,
  getUserData,
  removeCouncilPermission,
  addCouncilPermission,
  removeBuildingPermission,
  addBuildingPermission,
} from './helpers'

const noUserManagementConfig = {
  shortName: null,
  userId: null,
  isAdmin: null,
  role: null,
  username: null,
  phonenumber: null,
  pic: null,
  _id: null,
  twoFactorType: null,
  councilsAcList: [],
  buildingsAcList: [],
  selectedUser: null,
  isLoading: true,
  hasError: false,
}

interface SetUserManagementPayload {
  shortName: string
  userId: string
  isAdmin: string
  role: string
  pic: string
  username: string
  phonenumber: string
  _id?: string
  twoFactorType?: string
  councilsAcList?: []
  buildingsAcList?: []
}

const initialState: UserManagementState = { ...noUserManagementConfig }

export const userManagementSlice = createSlice({
  name: 'UserManagement',
  initialState,
  reducers: {
    fetchUser: (state) => {
      state.isLoading = true
      state.hasError = false
    },
    setUser: (state, action: PayloadAction<SetUserManagementPayload>) => {
      state.shortName = action.payload.shortName
      state.userId = action.payload.userId
      state.isAdmin = action.payload.isAdmin
      state.role = action.payload.role
      state.pic = action.payload.pic
      state.username = action.payload.username
      state.phonenumber = action.payload.phonenumber
      state._id = action.payload._id
      state.twoFactorType = action.payload.twoFactorType
      state.councilsAcList = action.payload.councilsAcList
      state.buildingsAcList = action.payload.buildingsAcList
      state.isLoading = false
      state.hasError = false
    },
    addUser: (state, action: PayloadAction<SetUserManagementPayload>) => {
      state.shortName = action.payload.shortName
      state.userId = action.payload.userId
      state.isAdmin = action.payload.isAdmin
      state.role = action.payload.role
      state.pic = action.payload.pic
      state.username = action.payload.username
      state.phonenumber = action.payload.phonenumber
      state.twoFactorType = action.payload.twoFactorType
      state.councilsAcList = action.payload.councilsAcList
      state.buildingsAcList = action.payload.buildingsAcList
      state.isLoading = false
      state.hasError = false
    },
    updateUser: (state, action: PayloadAction<SetUserManagementPayload>) => {
      state.shortName = action.payload.shortName
      state.userId = action.payload.userId
      state.isAdmin = action.payload.isAdmin
      state.role = action.payload.role
      state.pic = action.payload.pic
      state.username = action.payload.username
      state.phonenumber = action.payload.phonenumber
      state.twoFactorType = action.payload.twoFactorType
      state.councilsAcList = action.payload.councilsAcList
      state.buildingsAcList = action.payload.buildingsAcList
      state.isLoading = false
      state.hasError = false
    },
    fetchUserFailed: (state) => {
      state.shortName = null
      state.userId = null
      state.isAdmin = null
      state.role = null
      state.username = null
      state.phonenumber = null
      state.pic = null
      state._id = null
      state.twoFactorType = null
      state.councilsAcList = []
      state.buildingsAcList = []
      state.isLoading = false
      state.hasError = true
    },
  },
})

export const fetchUserManagementData = (accessToken: string) => async (dispatch) => {
  try {
    dispatch(fetchUser())
    // console.log(dispatch(fetchUsers()))
    const {
      shortName,
      userId,
      isAdmin,
      role,
      username,
      phonenumber,
      pic,
      _id,
      twoFactorType,
      councilsAcList,
      buildingsAcList,
    } = await getUserData(accessToken)
    dispatch(
      setUser({
        shortName,
        userId,
        isAdmin,
        role,
        username,
        phonenumber,
        pic,
        _id,
        twoFactorType,
        councilsAcList,
        buildingsAcList,
      }),
    )
  } catch (error) {
    message.error('User details not found')
    dispatch(fetchUserFailed())
  }
}

export const updateUserProfile = (user) => async (dispatch) => {
  try {
    const updatedUser = await userUpdateProfile(user)
    const {
      _id,
      shortName,
      isAdmin,
      role,
      userId,
      username,
      phonenumber,
      pic,
      twoFactorType,
      councilsAcList,
      buildingsAcList,
    } = updatedUser
    dispatch(
      setUser({
        shortName,
        userId,
        isAdmin,
        role,
        pic,
        username,
        phonenumber,
        _id,
        twoFactorType,
        councilsAcList,
        buildingsAcList,
      }),
    )
    message.success('User updated successfully')
    window.location.href = '/list-users';
  } catch (error) {
    message.error('User update failed')
    dispatch(fetchUserFailed())
  }
}

export const addUserProfile = (user) => async (dispatch) => {
  try {
    const addedUser = await userAddProfile(user);
    if (addedUser?.error) {
      message.error(addedUser.error);
    } else {
      message.success('User added successfully')
    }
  } catch (error) {
    // localStorage.clear()
    // sessionStorage.clear()
    message.error('User was unable to be added')

    dispatch(fetchUserFailed())
  }
}

export const addCouncilToList = (permissionUserId: string, id: string, name: string) => async (dispatch) => {
  try {
    const addPermissionToUser = await addCouncilPermission(permissionUserId, id, name)
    const {
      _id,
      shortName,
      isAdmin,
      role,
      userId,
      username,
      phonenumber,
      pic,
      twoFactorType,
      councilsAcList,
      buildingsAcList,
    } = addPermissionToUser
    dispatch(
      setUser({
        shortName,
        userId,
        isAdmin,
        role,
        pic,
        username,
        phonenumber,
        _id,
        twoFactorType,
        councilsAcList,
        buildingsAcList,
      }),
    )
    message.success('User updated')
  } catch (error) {
    message.error('User update failed')
    dispatch(fetchUserFailed())
  }
}

export const removeCouncilFromList = (id: string, ownerId: string) => async (dispatch) => {
  try {
    const removePermissionToUser = await removeCouncilPermission(id, ownerId)
    const {
      _id,
      shortName,
      isAdmin,
      role,
      userId,
      username,
      phonenumber,
      pic,
      twoFactorType,
      councilsAcList,
      buildingsAcList,
    } = removePermissionToUser
    dispatch(
      setUser({
        shortName,
        userId,
        isAdmin,
        role,
        pic,
        username,
        phonenumber,
        _id,
        twoFactorType,
        councilsAcList,
        buildingsAcList,
      }),
    )
    message.success('User updated')
  } catch (error) {
    message.error('User update failed')
    dispatch(fetchUserFailed())
  }
}

export const addBuildingToList =
  (permissionUserId: string, id: string, name: string, buildingId: string) => async (dispatch) => {
    try {
      const addPermissionToUser = await addBuildingPermission(permissionUserId, id, name, buildingId)
      const {
        _id,
        shortName,
        isAdmin,
        role,
        userId,
        username,
        phonenumber,
        pic,
        twoFactorType,
        councilsAcList,
        buildingsAcList,
      } = addPermissionToUser
      dispatch(
        setUser({
          shortName,
          userId,
          isAdmin,
          role,
          pic,
          username,
          phonenumber,
          _id,
          twoFactorType,
          councilsAcList,
          buildingsAcList,
        }),
      )
      message.success('User updated')
    } catch (error) {
      message.error('User update failed')
      dispatch(fetchUserFailed())
    }
  }

export const removeBuildingFromList = (id: string, buildingId: string) => async (dispatch) => {
  try {
    const removePermissionToUser = await removeBuildingPermission(id, buildingId)
    const {
      _id,
      shortName,
      isAdmin,
      role,
      userId,
      username,
      phonenumber,
      pic,
      twoFactorType,
      councilsAcList,
      buildingsAcList,
    } = removePermissionToUser
    dispatch(
      setUser({
        shortName,
        userId,
        isAdmin,
        role,
        pic,
        username,
        phonenumber,
        _id,
        twoFactorType,
        councilsAcList,
        buildingsAcList,
      }),
    )
    message.success('User updated')
  } catch (error) {
    message.error('User update failed')
    dispatch(fetchUserFailed())
  }
}

// Actions
export const { fetchUser, setUser, addUser, fetchUserFailed } = userManagementSlice.actions

export default userManagementSlice.reducer
